import React, { useMemo, useCallback } from 'react';
// --- CORRECTED IMPORT: useNavigate is from a router library ---
import { useNavigate } from 'react-router-dom';
import {
  useCalculator,
  useTotals,
  usePersonalization,
  useQuoteGenerator,
  useAnalytics,
  useAutoSaveVersion,
  sessionService,
  useAgentOffering, // --- ADDED: Import the hook for investment data ---
} from '@dopomogai/agent-kit';
import {
  createFrontendActions,
  interactiveToolRegistry,
  type FrontendToolContext,
} from '@dopomogai/chat-react';
import type { AgentVersion } from '@dopomogai/types';
import { AgentStickyRail } from './AgentStickyRail';
import { AgentPageLayout } from './AgentPageLayout';
import { Badge } from '@dopomogai/ui';


type Props = {
  version: AgentVersion;
};

export function AgentBuilderUI({ version }: Props) {
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { spec, state: calculatorState, actions: calculatorActions } = useCalculator();
  const { state: personalizationState, dispatch: personalizationDispatch } = usePersonalization();
  const {
    generateQuote,
    isLoading: isQuoteLoading,
    isEmailRequired,
    clearEmailRequiredFlag,
  } = useQuoteGenerator();

  // --- ADDED: Fetch investment data for this agent ---
  const { offering: investmentOffering, stats: investmentStats } = useAgentOffering(version.agent_id);

  const { totals } = useTotals(spec, calculatorState);

  const overrides = useMemo(
    () => ({
      calculator: {
        presetId: calculatorState.presetId || null,
        selectedModuleIds: Array.from(calculatorState.selectedModuleIds),
      },
      copy: {
        heroTitle: personalizationState.heroTitle || null,
        heroSubtitle: personalizationState.heroSubtitle || null,
        bullets: personalizationState.bullets || [],
      },
      brand: {
        logoUrl: personalizationState.logoUrl || null,
        primaryColor: personalizationState.primaryColor || null,
      },
    }),
    [calculatorState, personalizationState]
  );

  useAutoSaveVersion({
    versionId: version.id,
    overrides,
    visitorId: sessionService.getVisitorId(),
    onForked: (newVersion) => {
      navigate(`/versions/${newVersion.id}`, { replace: true });
      track('version_forked_client', {
        version_id: version.id,
        new_version_id: newVersion.id,
      });
    },
  });

  const handleGenerateQuote = useCallback(async () => {
    const newQuote = await generateQuote(version.id);
    if (newQuote?.id) {
      navigate(`/quotes/${newQuote.id}`);
    }
  }, [generateQuote, version.id, navigate]);

  const avatars = useMemo(() => {
    if (!version.avatars) return {};
    return {
      trainee: version.avatars.trainee?.url,
      junior: version.avatars.junior?.url,
      middle: version.avatars.middle?.url,
      senior: version.avatars.senior?.url,
      poster: version.avatars.base?.url,
    };
  }, [version.avatars]);

  const skills = useMemo(() => {
    const picks: Array<{ label: string; featureId: string; tooltip?: string }> = [];
    spec.groups.forEach((g) => {
      g.features
        .filter((f) => f.recommended)
        .slice(0, 2)
        .forEach((f) => picks.push({ label: f.label, featureId: f.id, tooltip: f.desc }));
    });
    return picks;
  }, [spec.groups]);

  const stats = useMemo(() => {
    const eta = totals.etaWeeks;
    return [
      { label: 'Setup', value: `$${totals.totalPriceUSD.toLocaleString()}` },
      { label: 'ETA', value: `${eta[0].toFixed(1)}–${eta[1].toFixed(1)} wks` },
    ];
  }, [totals]);

  const builderToolContext: FrontendToolContext = {
    selectPreset: (id) => {
      calculatorActions.setPreset(id);
    },
    setFeature: (id, enabled) => {
      const isSelected = calculatorState.selectedModuleIds.has(id);
      const shouldToggle = (enabled && !isSelected) || (!enabled && isSelected);
      if (shouldToggle) {
        calculatorActions.toggleModule(id);
      }
    },
    setPersonalizedCopy: (field, value, index) => {
      if (field === 'heroTitle') {
        personalizationDispatch({ type: 'SET_HERO_TITLE', payload: value });
      } else if (field === 'heroSubtitle') {
        personalizationDispatch({ type: 'SET_HERO_SUBTITLE', payload: value });
      } else if (field === 'bullets') {
        const bulletIndex = typeof index === 'number' ? index : 0;
        personalizationDispatch({ type: 'SET_BULLET', payload: { index: bulletIndex, text: value } });
      }
    },
    openSection: (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }),
    safeNavigate: (path) => navigate(path),
    fillFormField: () => {},
    submitLeadEmail: async (email: string) => {
      try {
        const res = await fetch('/leads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_email: email,
            lead_record_id: version.lead_record_id || sessionService.getLeadRecordId() || null,
          }),
          credentials: 'include',
        });
        if (!res.ok) {
          return { ok: false, error: `HTTP_${res.status}`, code: res.status === 400 ? 'invalid_email' : undefined };
        }
        return { ok: true, lead_id: version.lead_record_id || sessionService.getLeadRecordId() || undefined };
      } catch (e: any) {
        return { ok: false, error: e?.message || 'network_error', code: 'network_error' };
      }
    },
    openQuoteWithCurrentConfig: async () => {
      try {
        const q = await generateQuote(version.id);
        if (q?.id) {
          const url = `/quotes/${q.id}`;
          navigate(url);
          return { url };
        }
      } catch {
        // fall through
      }
      if (isEmailRequired) {
        if (typeof clearEmailRequiredFlag === 'function') clearEmailRequiredFlag();
        return { error: 'email_required', code: 'email_required' };
      }
      return { error: 'unknown_error' };
    },
  };

  const headlessTools = createFrontendActions(builderToolContext);

  // This function is required by AgentStickyRail -> AgentAvatarStage -> CosmeticBadgesOverlay
  const renderCosmeticBadge = (id: string) => (
    <Badge key={id} text={id.replace(/_/g, ' ')} accentColor="violet" />
  );
  
  const rightPane = (
    <AgentStickyRail
      avatars={avatars}
      renderCosmeticBadge={renderCosmeticBadge}
      skills={skills}
      stats={stats}
      selectedSkillIds={calculatorState.selectedModuleIds}
      planTier={totals.currentTier || 'trainee'}
      setupTotalUSD={totals.totalPriceUSD}
      etaDays={totals.etaDays}
      isQuoteLoading={isQuoteLoading}
      // --- FIXED: Use investment data from the hook instead of version.investment ---
      investmentOffering={investmentOffering}
      investmentStats={investmentStats}
      agentId={version.agent_id}
      onInvestClick={(agentId) => navigate(`/invest/${agentId}`)}
      chatbotTools={{
        toolRegistry: interactiveToolRegistry,
        headlessTools,
        headlessContext: builderToolContext,
      }}
      onSkillClick={(featureId) => calculatorActions.toggleModule(featureId)}
      onGenerateQuote={handleGenerateQuote}
    />
  );

  const leftPane = <div>Left pane content</div>;

  return <AgentPageLayout left={leftPane} right={rightPane} />;
}